import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "segment scheme-light content" }
const _hoisted_2 = { class: "container-content" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "col-10-10 col-10-md-3 col-10-xxl-2" }
const _hoisted_5 = { class: "facets funding-programs-list-facets" }
const _hoisted_6 = { class: "facet-reset" }
const _hoisted_7 = { class: "col-10-10 col-10-md-7 col-10-xxl-8" }
const _hoisted_8 = {
  key: 1,
  class: "pagination pagination-top"
}
const _hoisted_9 = { class: "box-grid-vertical" }
const _hoisted_10 = { class: "card box-grid-horizontal" }
const _hoisted_11 = { class: "card-title" }
const _hoisted_12 = ["href"]
const _hoisted_13 = { class: "card-eyebrow" }
const _hoisted_14 = {
  key: 0,
  class: "card-eyebrow-text"
}
const _hoisted_15 = { class: "card-text" }
const _hoisted_16 = { class: "list-links" }
const _hoisted_17 = /*#__PURE__*/_createElementVNode("i", { class: "icon icon-start" }, null, -1 /* HOISTED */)
const _hoisted_18 = { key: 0 }
const _hoisted_19 = /*#__PURE__*/_createElementVNode("i", { class: "icon icon-career-stages" }, null, -1 /* HOISTED */)
const _hoisted_20 = { key: 1 }
const _hoisted_21 = /*#__PURE__*/_createElementVNode("i", { class: "icon icon-keywords" }, null, -1 /* HOISTED */)
const _hoisted_22 = { class: "visually-hidden" }
const _hoisted_23 = {
  key: 2,
  class: "pagination"
}

import { inject, nextTick, onMounted, ref, watch } from 'vue';
import FacetsContainer from './components/FacetsContainer.vue';
import PaginationBar from './components/PaginationBar.vue';
import SkipLink from './components/SkipLink.vue';
import axios, { AxiosResponse } from 'axios';
import {
    parseSolrResponse,
    ParsedSolrResponseData,
    RequestUrl,
    SolrArguments,
    SolrResponseData, SolrDocument
} from '../modules/solr'; // eslint-disable-line import/no-relative-parent-imports
import { breakpointsBootstrapV5, useBreakpoints } from '@vueuse/core';

interface FundingProgramDocument extends SolrDocument {
    programType: string;
    programChannel: string;
    programSubmissionStatus: string;
    programHasOpenSubmissions: boolean;
    programKeywords: string;
    programVolume: string;
    programCareerLevels: string;
    programStartTimestamp: number;
    programEndTimestamp: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FundingProgramsList',
  props: {
    endpoint: {},
    pluginNamespace: {},
    arguments: {}
  },
  setup(__props: any) {

const props = __props;



const i18n = inject('i18n') as { [key: string]: string };
const locale = inject('locale') as string || 'de-AT';
const defaultArguments: SolrArguments = {
    filter: ['type:programs'], // fallback
    page: '1', // required
    perPage: '0', // placeholder, see setPerPage()
    q: '*', // required
    sort: 'rootlineSorting asc' // fallback
};
const currentTimestamp: number = Math.floor(Date.now() / 1000);
const fundingProgramCards = ref<HTMLElement | null>(null);

const userFacetFields = ['programChannel', 'programHasOpenSubmissions', 'category']; // array order defines display order
const initialArguments: SolrArguments = { ...defaultArguments, ...JSON.parse(props.arguments) as SolrArguments };

class FundingProgramsRequestUrl extends RequestUrl {
    public setPerPage (): void {
        let perPage = '4';
        if (largerThanMd.value) {
            perPage = '12';
        } else if (largerThanSm.value) {
            perPage = '8';
        }
        this.url.searchParams.set(`${props.pluginNamespace}[perPage]`, perPage);
    }
}

const initialUrl = FundingProgramsRequestUrl.buildFromArguments(props.endpoint, props.pluginNamespace, initialArguments) as FundingProgramsRequestUrl;
let currentUrl = new FundingProgramsRequestUrl(initialUrl);

const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const largerThanSm = breakpoints.greater('sm');
const largerThanMd = breakpoints.greater('md');

const currentSolrResponse = ref<ParsedSolrResponseData>({
    facets: [],
    documents: [],
    currentPageNumber: 1,
    totalNumberOfPages: 1
});
const fundingProgramDocuments = ref<FundingProgramDocument[]>([]);

watch(fundingProgramDocuments, async () => {
    await nextTick();
    window.initCardWidgets();
});

function loadFundingProgramsAfterFacetChange (name: string, value: string | string[], type?: string): void {
    const url = new FundingProgramsRequestUrl(currentUrl);

    if (type === undefined && Array.isArray(value)) {
        url.removeFilter(name);
        value.forEach((selectElement: string) => {
            url.addFilterParameter(`${name}:${selectElement}`);
        });
    } else {
        if (typeof value === 'string') {
            switch (type) {
                case 'add':
                    url.addFilterParameter(`${name}:${value}`);
                    break;
                case 'remove':
                    url.removeFilterParameter(`${name}:${value}`);
                    break;
                case 'set':
                    url.removeFilter(name);
                    url.addFilterParameter(`${name}:${value}`);
                    break;
                default:
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    console.trace(`Invalid change type '${type}' (facet change).`);
            }
        }
    }

    url.setPerPage();
    // Reset to page 1
    url.setPage(1);
    axios.get(url.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        fundingProgramDocuments.value = currentSolrResponse.value.documents as FundingProgramDocument[];
        return undefined;
    }).catch(() => console.trace('Loading of funding programs failed (facet change).'));

    currentUrl = url;
}

function loadFundingProgramsAfterPageSelect (page: number): void {
    const url = new FundingProgramsRequestUrl(currentUrl);
    url.setPerPage();
    url.setPage(page);

    axios.get(url.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        fundingProgramDocuments.value = currentSolrResponse.value.documents as FundingProgramDocument[];

        fundingProgramCards.value?.setAttribute('tabindex', '0');
        fundingProgramCards.value?.focus();
        fundingProgramCards.value?.removeAttribute('tabindex');
        fundingProgramCards.value?.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
        });

        return undefined;
    }).catch(() => console.trace('Loading of funding programs failed (page select).'));

    currentUrl = url;
}

function loadInitialList (): Promise<void|undefined> {
    const url = new FundingProgramsRequestUrl(initialUrl);
    url.setPerPage();
    currentUrl = url;
    return axios.get(url.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        fundingProgramDocuments.value = currentSolrResponse.value.documents as FundingProgramDocument[];
        return undefined;
    });
}

function resetFilter (): void {
    // Explicitly set facets to 'all' and reload list, ignoring initially set filters (via URL etc.)
    const url = FundingProgramsRequestUrl.buildFromArguments(props.endpoint, props.pluginNamespace, initialArguments) as FundingProgramsRequestUrl;

    axios.get(url.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        fundingProgramDocuments.value = currentSolrResponse.value.documents as FundingProgramDocument[];

        // Reset URL
        const baseUrl = window.location.href.split('?')[0];
        window.history.pushState({}, document.title, baseUrl);

        return undefined;
    }).catch(() => console.trace('Loading of funding programs failed (reset filter).'));

    currentUrl = url;
}

loadInitialList().catch(() => console.trace('Initial loading of funding programs failed.'));


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(FacetsContainer, {
              facets: currentSolrResponse.value.facets,
              onChange: loadFundingProgramsAfterFacetChange,
              onChangeMultiple: loadFundingProgramsAfterFacetChange
            }, null, 8 /* PROPS */, ["facets"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("button", {
              class: "button button-reset",
              onClick: resetFilter
            }, _toDisplayString(_unref(i18n).resetFilter), 1 /* TEXT */)
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          (currentSolrResponse.value.totalNumberOfPages > 1)
            ? (_openBlock(), _createBlock(SkipLink, {
                key: 0,
                to: "#funding-programs-list-cards"
              }))
            : _createCommentVNode("v-if", true),
          (currentSolrResponse.value.totalNumberOfPages > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createVNode(PaginationBar, {
                  current: currentSolrResponse.value.currentPageNumber,
                  total: currentSolrResponse.value.totalNumberOfPages,
                  onSelect: loadFundingProgramsAfterPageSelect
                }, null, 8 /* PROPS */, ["current", "total"])
              ]))
            : _createCommentVNode("v-if", true),
          _createElementVNode("div", {
            id: "funding-programs-list-cards",
            ref_key: "fundingProgramCards",
            ref: fundingProgramCards,
            class: "funding-programs-list inner-row"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fundingProgramDocuments.value, (document) => {
              return (_openBlock(), _createElementBlock("div", {
                key: document.id,
                class: "funding-programs-list-item box box-grid box-grid-top-closed col-2-2"
              }, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("a", {
                        href: document.url,
                        "data-card-link": "href"
                      }, _toDisplayString(document.title), 9 /* TEXT, PROPS */, _hoisted_12)
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      (document.programChannel)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(document.programChannel), 1 /* TEXT */))
                        : _createCommentVNode("v-if", true)
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("ul", _hoisted_16, [
                        _createElementVNode("li", null, [
                          _hoisted_17,
                          _createElementVNode("strong", null, _toDisplayString(_unref(i18n).status) + ":", 1 /* TEXT */),
                          _createElementVNode("span", null, _toDisplayString(document.programSubmissionStatus), 1 /* TEXT */)
                        ]),
                        (document.programCareerLevels)
                          ? (_openBlock(), _createElementBlock("li", _hoisted_18, [
                              _hoisted_19,
                              _createElementVNode("strong", null, _toDisplayString(_unref(i18n).careerLevel) + ":", 1 /* TEXT */),
                              _createElementVNode("span", null, _toDisplayString(document.programCareerLevels), 1 /* TEXT */)
                            ]))
                          : _createCommentVNode("v-if", true),
                        (document.programKeywords)
                          ? (_openBlock(), _createElementBlock("li", _hoisted_20, [
                              _hoisted_21,
                              _createElementVNode("span", _hoisted_22, _toDisplayString(_unref(i18n).summary) + ":", 1 /* TEXT */),
                              _createElementVNode("span", null, _toDisplayString(document.programKeywords), 1 /* TEXT */)
                            ]))
                          : _createCommentVNode("v-if", true)
                      ])
                    ])
                  ])
                ])
              ]))
            }), 128 /* KEYED_FRAGMENT */))
          ], 512 /* NEED_PATCH */),
          (currentSolrResponse.value.totalNumberOfPages > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                _createVNode(PaginationBar, {
                  current: currentSolrResponse.value.currentPageNumber,
                  total: currentSolrResponse.value.totalNumberOfPages,
                  onSelect: loadFundingProgramsAfterPageSelect
                }, null, 8 /* PROPS */, ["current", "total"])
              ]))
            : _createCommentVNode("v-if", true)
        ])
      ])
    ])
  ]))
}
}

})